import { importAesKey, base64ToBytes, decryptSegmentBuffer, segmentAad } from "./crypto.js";
import { AudioWatermark } from "./audio-watermark.js";
import { VisualWatermark } from "./watermark.js";
import { VideoWatermark } from "./watermark-video.js";
import { Security } from "./security.js";

const HEARTBEAT_MS = 15000;
const BUFFER_AHEAD_S = 40;
const KEEP_BEHIND_S = 20;
const FETCH_TIMEOUT_MS = 30000;
const FETCH_RETRIES = 3;

const IDLE_MS = 3000;
const SEEK_KEY_S = 5;
const SEEK_TAP_S = 10;
const SPEEDS = [0.5, 1, 1.25, 1.5, 2];
const VOL_KEY = "rightra:player:volume";
const COMPACT_PX = 520;
const TINY_PX = 380;
const STYLE_ID = "rtr-player-styles";

export class RightraPlaybackError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "RightraPlaybackError";
    this.code = code;
    this.cause = cause;
  }
}

const FRIENDLY = {
  MANIFEST_INVALID: "This video's data could not be read.",
  SEGMENT_MISMATCH: "Part of this video could not be verified.",
  DECRYPT_FAILED: "This video could not be unlocked.",
  MEDIA_ERROR: "Your browser could not play this video.",
  UNSUPPORTED: "This video format is not supported in this browser.",
  NETWORK: "Network problem. Check your connection and try again.",
  FETCH_FAILED: "The video could not be loaded.",
  SESSION_EXPIRED: "Your viewing session has ended.",
  BUFFER_FULL: "Your browser ran out of video memory.",
  WATERMARK_FAILED: "This video could not be protected.",
  UNKNOWN: "Something went wrong.",
};

const sleep = (ms, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException("Aborted", "AbortError"));
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };
    const t = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    signal?.addEventListener("abort", onAbort, { once: true });
  });

/**
 * The manifest URL comes from the creator's own upload handler and is stored
 * verbatim by Rightra. It is used as-is. The SDK does not know or care where
 * the segments live; the manifest tells the player the file names, and the
 * player resolves them against this URL.
 */
function resolveManifestUrl(originalUrl) {
  try {
    return new URL(originalUrl, window.location.href).toString();
  } catch {
    return originalUrl;
  }
}

function resolveSegmentUrl(manifestUrl, seg) {
  const file = seg.file || (seg.url ? String(seg.url).split("?")[0].split("/").pop() : null);
  if (!file) throw new RightraPlaybackError("MANIFEST_INVALID", `Segment ${seg.index} has no file name.`);
  const u = new URL(manifestUrl, window.location.href);
  if (u.searchParams.has("file")) {
    u.searchParams.set("file", file);
    return u.toString();
  }
  return new URL(file, u).toString();
}

async function fetchBinary(url, signal) {
  let lastErr = null;
  for (let attempt = 0; attempt <= FETCH_RETRIES; attempt++) {
    const ctl = new AbortController();
    const onAbort = () => ctl.abort();
    signal?.addEventListener("abort", onAbort, { once: true });
    const timer = setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS);
    try {
      const res = await fetch(url, { cache: "no-store", signal: ctl.signal });
      if (res.ok) {
        const type = res.headers.get("content-type") || "";
        if (/text\/html/i.test(type)) {
          const e = new RightraPlaybackError(
            "FETCH_FAILED",
            "Storage returned a web page instead of binary data. Check the storage URL / proxy."
          );
          e.fatal = true;
          throw e;
        }
        return new Uint8Array(await res.arrayBuffer());
      }
      const e = new RightraPlaybackError("FETCH_FAILED", `Storage returned HTTP ${res.status}.`);
      e.status = res.status;
      if (res.status < 500 && res.status !== 429) {
        e.fatal = true;
        throw e;
      }
      lastErr = e;
    } catch (e) {
      if (signal?.aborted || e.fatal) throw e;
      lastErr = e;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    }
    if (attempt < FETCH_RETRIES) await sleep(500 * 2 ** attempt, signal);
  }
  throw new RightraPlaybackError("NETWORK", "Could not download from storage.", lastErr);
}

const SVG_NS = "http://www.w3.org/2000/svg";
const ICONS = {
  play: "M8 5v14l11-7z",
  pause: "M6 5h4v14H6zM14 5h4v14h-4z",
  replay: "M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z",
  volHigh:
    "M3 9v6h4l5 5V4L7 9H3zm13.5 3A4.5 4.5 0 0 0 14 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z",
  volLow: "M18.5 12A4.5 4.5 0 0 0 16 7.97v8.05c1.48-.73 2.5-2.25 2.5-4.02zM5 9v6h4l5 5V4L9 9H5z",
  volMuted:
    "M16.5 12A4.5 4.5 0 0 0 14 7.97v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51A8.8 8.8 0 0 0 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06a8.99 8.99 0 0 0 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z",
  fs: "M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z",
  fsExit: "M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z",
  more: "M6 10a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm12 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm-6 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z",
  back: "M11 18V6l-8.5 6 8.5 6zm.5-6l8.5 6V6l-8.5 6z",
  fwd: "M4 18l8.5-6L4 6v12zm9-12v12l8.5-6L13 6z",
  warn: "M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z",
  dl: "M5 20h14v-2H5v2zM13 4v8.17l2.59-2.58L17 11l-5 5-5-5 1.41-1.41L11 12.17V4h2z",
};

function icon(name) {
  const s = document.createElementNS(SVG_NS, "svg");
  s.setAttribute("viewBox", "0 0 24 24");
  s.setAttribute("aria-hidden", "true");
  s.setAttribute("focusable", "false");
  const p = document.createElementNS(SVG_NS, "path");
  p.setAttribute("d", ICONS[name]);
  p.setAttribute("fill", "currentColor");
  s.appendChild(p);
  return s;
}
const setIcon = (node, name) => node.replaceChildren(icon(name));

function el(tag, cls, attrs) {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (attrs) for (const k in attrs) n.setAttribute(k, attrs[k]);
  return n;
}
function iconBtn(cls, label, iconName) {
  const b = el("button", `rtr-b ${cls}`, { type: "button", "aria-label": label, title: label });
  if (iconName) b.appendChild(icon(iconName));
  return b;
}
const fmt = (s) => {
  const t = Number.isFinite(s) && s > 0 ? Math.floor(s) : 0;
  return `${Math.floor(t / 60)}:${String(t % 60).padStart(2, "0")}`;
};
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
const getMediaSourceCtor = () => window.MediaSource || window.ManagedMediaSource || null;

const CSS = `
.rtr-root{--rtr-accent:#BC8BBC;isolation:isolate;overflow:hidden;background:#000;color:#fff;max-width:100%;min-width:0;box-sizing:border-box;outline:none;font:500 13px/1.2 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif;-webkit-user-select:none;user-select:none;-webkit-tap-highlight-color:transparent;touch-action:manipulation}
.rtr-root *{box-sizing:border-box}
.rtr-root [hidden]{display:none!important}
.rtr-pos{position:relative}
.rtr-root:focus-visible{box-shadow:inset 0 0 0 2px var(--rtr-accent)}
.rtr-idle{cursor:none}
.rtr-pfs{position:fixed!important;inset:0;width:100%!important;height:100%!important;max-width:none!important;z-index:2147483600}
.rtr-noscroll{overflow:hidden!important}
.rtr-video{display:block;width:100%;height:100%;background:#000;object-fit:contain}
.rtr-gesture{position:absolute;inset:0;z-index:1}
.rtr-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}
.rtr-spinner{position:absolute;left:50%;top:50%;width:44px;height:44px;margin:-22px 0 0 -22px;border:3px solid rgba(255,255,255,.25);border-top-color:var(--rtr-accent);border-radius:50%;animation:rtr-spin .8s linear infinite;display:none;z-index:4;pointer-events:none}
.rtr-loading .rtr-spinner{display:block}
.rtr-big{position:absolute;left:50%;top:50%;width:68px;height:68px;margin:-34px 0 0 -34px;padding:0;border:0;border-radius:50%;background:var(--rtr-accent);color:#111;display:none;align-items:center;justify-content:center;cursor:pointer;z-index:4;box-shadow:0 6px 24px rgba(0,0,0,.45);transition:transform .15s}
.rtr-big:hover{transform:scale(1.06)}
.rtr-big svg{width:34px;height:34px}
.rtr-paused:not(.rtr-loading):not(.rtr-error):not(.rtr-hold) .rtr-big{display:flex}
.rtr-rip{position:absolute;top:0;bottom:0;width:34%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:4px;opacity:0;pointer-events:none;z-index:3;font-size:12px;background:radial-gradient(circle,rgba(255,255,255,.24),rgba(255,255,255,0) 70%)}
.rtr-rip svg{width:28px;height:28px}
.rtr-rip-l{left:0}.rtr-rip-r{right:0}
.rtr-go{animation:rtr-rip .6s ease-out}
.rtr-toast{position:absolute;top:12px;left:50%;transform:translateX(-50%);max-width:calc(100% - 24px);padding:6px 12px;border-radius:999px;background:rgba(0,0,0,.68);font-size:12px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;opacity:0;transition:opacity .2s;pointer-events:none;z-index:6}
.rtr-show{opacity:1}
.rtr-fetch{position:absolute;left:0;right:0;bottom:0;height:2px;z-index:6;overflow:hidden;opacity:0;transition:opacity .2s;pointer-events:none}
.rtr-fetching .rtr-fetch{opacity:1}
.rtr-fetch::after{content:"";position:absolute;top:0;bottom:0;width:35%;background:var(--rtr-accent);animation:rtr-slide 1.1s ease-in-out infinite}
.rtr-controls{position:absolute;left:0;right:0;bottom:0;z-index:5;display:flex;align-items:center;gap:4px;padding:30px 10px 8px;background:linear-gradient(to top,rgba(0,0,0,.78),rgba(0,0,0,0));transition:opacity .25s}
.rtr-idle .rtr-controls{opacity:0;pointer-events:none}
.rtr-b{flex:none;width:36px;height:36px;padding:0;border:0;border-radius:8px;background:transparent;color:#fff;display:inline-flex;align-items:center;justify-content:center;cursor:pointer}
.rtr-b:hover{background:rgba(255,255,255,.16)}
.rtr-b:focus-visible,.rtr-item:focus-visible,.rtr-cta:focus-visible,.rtr-big:focus-visible{outline:2px solid var(--rtr-accent);outline-offset:1px}
.rtr-b svg{width:22px;height:22px}
.rtr-txt{width:auto;min-width:40px;padding:0 8px;font-size:13px;font-variant-numeric:tabular-nums}
.rtr-prog{position:relative;flex:1 1 auto;min-width:0;height:28px;margin:0 6px;display:flex;align-items:center;touch-action:none}
.rtr-track{position:absolute;left:0;right:0;top:50%;height:4px;margin-top:-2px;border-radius:3px;background:rgba(255,255,255,.25);overflow:hidden;transition:height .12s,margin-top .12s}
.rtr-prog:hover .rtr-track,.rtr-scrub .rtr-track,.rtr-prog:focus-within .rtr-track{height:7px;margin-top:-3.5px}
.rtr-buf,.rtr-played{position:absolute;left:0;top:0;bottom:0;width:100%;transform-origin:0 50%}
.rtr-buf{background:rgba(255,255,255,.4);transform:scaleX(var(--rtr-b,0));transition:transform .2s linear}
.rtr-played{background:var(--rtr-accent);transform:scaleX(var(--rtr-p,0));transition:transform .1s linear}
.rtr-scrub .rtr-played{transition:none}
.rtr-range,.rtr-volrange{-webkit-appearance:none;appearance:none;background:transparent;margin:0;cursor:pointer}
.rtr-range{position:absolute;inset:0;width:100%;height:100%}
.rtr-range::-webkit-slider-runnable-track{height:28px;background:transparent}
.rtr-range::-moz-range-track{height:28px;background:transparent}
.rtr-range::-webkit-slider-thumb{-webkit-appearance:none;width:14px;height:14px;margin-top:7px;border-radius:50%;border:0;background:#fff;box-shadow:0 0 0 3px rgba(188,139,188,.55);transform:scale(0);transition:transform .12s}
.rtr-range::-moz-range-thumb{width:14px;height:14px;border-radius:50%;border:0;background:#fff;box-shadow:0 0 0 3px rgba(188,139,188,.55);transform:scale(0);transition:transform .12s}
.rtr-prog:hover .rtr-range::-webkit-slider-thumb,.rtr-scrub .rtr-range::-webkit-slider-thumb,.rtr-range:focus-visible::-webkit-slider-thumb{transform:scale(1)}
.rtr-prog:hover .rtr-range::-moz-range-thumb,.rtr-scrub .rtr-range::-moz-range-thumb,.rtr-range:focus-visible::-moz-range-thumb{transform:scale(1)}
.rtr-range:focus{outline:none}
.rtr-tip{position:absolute;bottom:26px;transform:translateX(-50%);padding:3px 7px;border-radius:5px;background:rgba(0,0,0,.8);font:500 11px/1 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;opacity:0;pointer-events:none;white-space:nowrap}
.rtr-prog:hover .rtr-tip{opacity:1}
.rtr-time{flex:none;margin:0 4px;font:500 12px/1 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;color:rgba(255,255,255,.72);white-space:nowrap}
.rtr-qual{flex:none;padding:3px 6px;border-radius:5px;background:rgba(255,255,255,.14);font-size:11px;letter-spacing:.03em;color:rgba(255,255,255,.85)}
.rtr-more{display:none}
.rtr-pop{position:absolute;z-index:8;display:none;min-width:150px;max-width:calc(100% - 16px);max-height:calc(100% - 72px);overflow:auto;padding:6px;border-radius:10px;border:1px solid rgba(255,255,255,.12);background:rgba(18,18,22,.97);box-shadow:0 10px 30px rgba(0,0,0,.5)}
.rtr-open{display:block}
.rtr-list{display:flex;flex-direction:column}
.rtr-item{display:flex;align-items:center;justify-content:space-between;width:100%;padding:8px 10px;border:0;border-radius:6px;background:transparent;color:#fff;font:inherit;text-align:left;cursor:pointer}
.rtr-item:hover{background:rgba(255,255,255,.12)}
.rtr-item[aria-checked="true"]{color:var(--rtr-accent);font-weight:700}
.rtr-item[aria-checked="true"]::after{content:"\\2713";margin-left:12px}
.rtr-label{padding:6px 10px 2px;font-size:11px;letter-spacing:.06em;text-transform:uppercase;color:rgba(255,255,255,.55)}
.rtr-volrow{display:flex;align-items:center;gap:6px;padding:2px 4px}
.rtr-volrange{width:110px;max-width:100%;height:20px}
.rtr-volrange::-webkit-slider-runnable-track{height:4px;border-radius:2px;background:linear-gradient(to right,var(--rtr-accent) var(--rtr-v,100%),rgba(255,255,255,.3) var(--rtr-v,100%))}
.rtr-volrange::-moz-range-track{height:4px;border-radius:2px;background:linear-gradient(to right,var(--rtr-accent) var(--rtr-v,100%),rgba(255,255,255,.3) var(--rtr-v,100%))}
.rtr-volrange::-webkit-slider-thumb{-webkit-appearance:none;width:12px;height:12px;margin-top:-4px;border-radius:50%;border:0;background:#fff}
.rtr-volrange::-moz-range-thumb{width:12px;height:12px;border-radius:50%;border:0;background:#fff}
.rtr-qrow{padding:8px 10px;font-size:12px;color:rgba(255,255,255,.7)}
.rtr-msg{position:absolute;inset:0;z-index:9;display:none;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:16px;text-align:center;background:rgba(10,10,14,.86)}
.rtr-hold .rtr-hold-msg,.rtr-error .rtr-err-msg{display:flex}
.rtr-msg svg{width:34px;height:34px;color:var(--rtr-accent)}
.rtr-msg-t{margin:0;max-width:100%;font-size:14px;line-height:1.4;overflow-wrap:anywhere}
.rtr-code{font:500 11px/1 ui-monospace,SFMono-Regular,Menlo,Consolas,monospace;color:rgba(255,255,255,.6);letter-spacing:.06em}
.rtr-cta{padding:8px 18px;border:0;border-radius:999px;background:var(--rtr-accent);color:#111;font:600 13px/1 inherit;cursor:pointer}
.rtr-cta:hover{filter:brightness(1.08)}
.rtr-compact .rtr-volbtn,.rtr-compact .rtr-speedbtn,.rtr-compact .rtr-qual{display:none}
.rtr-compact .rtr-more{display:inline-flex}
.rtr-compact .rtr-big{width:54px;height:54px;margin:-27px 0 0 -27px}
.rtr-novol .rtr-volbtn,.rtr-novol .rtr-volrow{display:none}
@media (pointer:coarse){.rtr-b{width:40px;height:40px}.rtr-range::-webkit-slider-thumb{transform:scale(1)}.rtr-range::-moz-range-thumb{transform:scale(1)}}
.rtr-tiny .rtr-controls{gap:0;padding:26px 6px 6px}
.rtr-tiny .rtr-b{width:34px;height:34px}
.rtr-tiny .rtr-b svg{width:20px;height:20px}
.rtr-tiny .rtr-prog{margin:0 4px}
.rtr-tiny .rtr-time{margin:0 2px;font-size:10px}
.rtr-tiny .rtr-msg-t{font-size:13px}
@keyframes rtr-spin{to{transform:rotate(360deg)}}
@keyframes rtr-slide{from{left:-35%}to{left:100%}}
@keyframes rtr-rip{0%{opacity:1}100%{opacity:0}}
@media (prefers-reduced-motion:reduce){.rtr-root *{transition-duration:.01ms!important}.rtr-go{animation-duration:.01ms}}
`;

function ensureStyles() {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement("style");
  s.id = STYLE_ID;
  s.textContent = CSS;
  document.head.appendChild(s);
}

export class RightraPlayer {
  constructor(container, options) {
    const bad = (m) => new RightraPlaybackError("UNKNOWN", m);
    if (!container) throw bad("RightraPlayer requires a container element.");
    if (!options?.client) throw bad("RightraPlayer requires a client.");
    if (!options?.contentId) throw bad("RightraPlayer requires a contentId.");
    if (!options?.manifestUrl) throw bad("RightraPlayer requires a manifestUrl.");
    if (!options.keyBytes && !options.keyMaterial) throw bad("RightraPlayer requires a key.");

    this.container = container;
    this.client = options.client;
    this.contentId = options.contentId;
    this.manifestUrl = resolveManifestUrl(options.manifestUrl);
    try {
      this.keyBytes = options.keyBytes || base64ToBytes(options.keyMaterial);
    } catch (e) {
      throw new RightraPlaybackError("UNKNOWN", "The playback key is not valid.", e);
    }
    this.watermarkPayload = options.watermarkPayload;
    this.sessionCode = Number.isFinite(Number(options.sessionCode))
      ? Number(options.sessionCode) >>> 0
      : null;
    this.sessionId = options.sessionId;
    this.onError = options.onError || (() => {});
    this.onEnded = options.onEnded || (() => {});
    this.downloadAllowed = options.downloadAllowed === true;
    this._downloading = false;
    this.onRetry = typeof options.onRetry === "function" ? options.onRetry : null;
    this.watermarkOptions = { visual: true, audio: true, video: true, ...(options.watermark || {}) };
    this.securityOptions = {
      devtools: "log",
      tabSwitch: "log",
      tabSwitchThresholdMs: 20000,
      ...(options.security || {}),
    };

    this.manifest = null;
    this.version = 2;
    this.initSeg = null;
    this.mediaSegs = [];
    this.timeIndexed = false;
    this.key = null;
    this.mediaSource = null;
    this.sourceBuffer = null;
    this.audioWatermark = null;
    this.visualWatermark = null;
    this.videoWatermark = null;
    this._managed = false;
    this._objectUrl = null;
    this._destroyed = false;
    this._failed = false;
    this._retrying = false;
    this._resetting = false;
    this._initDone = false;
    this._loaded = false;
    this._gen = 0;
    this._next = 0;
    this._appended = 0;
    this._abort = null;
    this._appendChain = Promise.resolve();
    this._waiters = new Set();
    this._sbError = null;
    this._heartbeat = null;
    this._resumeAt = null;
    this._skipSeek = false;
    this._endedSent = false;
    this._lastReport = Object.create(null);

    this._nodes = [];
    this._cleanup = [];
    this._holdReasons = new Set();
    this._wasPlaying = false;
    this._scrubbing = false;
    this._kbd = false;
    this._pfs = false;
    this._fetchDepth = 0;
    this._timeTxt = "";
    this._volWidgets = [];
    this._speedItems = [];
    this._pops = [];
    this._lastTap = null;
    this._chain = null;
    this._lastPointerType = "mouse";
    this._lastError = null;
    this._raf = 0;
    this._lastUi = 0;

    ensureStyles();
    this._buildUi();

    this.security = new Security(this.container, {
      ...this.securityOptions,
      onEvent: (type, detail) => this._onSecurityEvent(type, detail),
    });
    this.security.attach();
  }

  _buildUi() {
    const root = this.container;
    this._prev = {
      tabindex: root.getAttribute("tabindex"),
      role: root.getAttribute("role"),
      label: root.getAttribute("aria-label"),
    };
    root.classList.add("rtr-root", "rtr-paused", "rtr-loading");
    if (getComputedStyle(root).position === "static") root.classList.add("rtr-pos");
    root.tabIndex = 0;
    root.setAttribute("role", "group");
    root.setAttribute("aria-label", "Video player");

    const add = (node) => {
      root.appendChild(node);
      this._nodes.push(node);
      return node;
    };
    const on = (target, type, fn, opts) => {
      target.addEventListener(type, fn, opts);
      this._cleanup.push(() => target.removeEventListener(type, fn, opts));
    };

    const v = (this.video = el("video", "rtr-video"));
    v.controls = false;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.setAttribute("webkit-playsinline", "");
    v.preload = "auto";
    v.disablePictureInPicture = true;
    v.disableRemotePlayback = true;
    v.setAttribute("controlsList", "nodownload noremoteplayback");
    add(v);

    this.gesture = add(el("div", "rtr-gesture"));
    on(this.gesture, "pointerup", (e) => this._onGesture(e));
    on(this.gesture, "dblclick", () => {
      if (this._lastPointerType === "mouse") this._toggleFullscreen();
    });

    this._ripL = add(el("div", "rtr-rip rtr-rip-l"));
    this._ripR = add(el("div", "rtr-rip rtr-rip-r"));
    this._ripL.append(icon("back"), document.createTextNode(`${SEEK_TAP_S} s`));
    this._ripR.append(icon("fwd"), document.createTextNode(`${SEEK_TAP_S} s`));

    this._spinner = add(el("div", "rtr-spinner", { "aria-hidden": "true" }));

    this._big = add(el("button", "rtr-big", { type: "button", "aria-label": "Play" }));
    this._big.appendChild(icon("play"));
    on(this._big, "click", () => this._togglePlay());

    this._toastEl = add(el("div", "rtr-toast", { "aria-hidden": "true" }));
    this._live = add(el("div", "rtr-sr", { "aria-live": "polite", role: "status" }));

    const bar = (this.controls = add(el("div", "rtr-controls")));

    this._playBtn = iconBtn("rtr-play", "Play", "play");
    on(this._playBtn, "click", () => this._togglePlay());

    this._volSupported = this._detectVolumeSupport();
    if (!this._volSupported) root.classList.add("rtr-novol");
    this._volBtn = iconBtn("rtr-volbtn", "Volume", "volHigh");
    this._volBtn.setAttribute("aria-haspopup", "true");
    this._volBtn.setAttribute("aria-expanded", "false");

    const prog = (this._prog = el("div", "rtr-prog"));
    const track = el("div", "rtr-track");
    this._bufFill = el("div", "rtr-buf");
    this._playedFill = el("div", "rtr-played");
    track.append(this._bufFill, this._playedFill);
    const range = (this._range = el("input", "rtr-range", {
      type: "range", min: "0", max: "1000", step: "1", value: "0",
      "aria-label": "Seek", "aria-valuetext": "0:00 of 0:00",
    }));
    this._tip = el("div", "rtr-tip", { "aria-hidden": "true" });
    prog.append(track, range, this._tip);
    this._wireProgress(prog, range);

    this._time = el("span", "rtr-time", { "aria-hidden": "true" });
    this._time.textContent = "0:00 / 0:00";
    this._qual = el("span", "rtr-qual", { title: "Source quality" });
    this._qual.hidden = true;

    this._speedBtn = el("button", "rtr-b rtr-txt rtr-speedbtn", {
      type: "button", "aria-label": "Playback speed", title: "Playback speed",
      "aria-haspopup": "true", "aria-expanded": "false",
    });
    this._speedBtn.textContent = "1\u00d7";

    this._moreBtn = iconBtn("rtr-more", "More options", "more");
    this._moreBtn.setAttribute("aria-haspopup", "true");
    this._moreBtn.setAttribute("aria-expanded", "false");

    this._fsBtn = iconBtn("rtr-fsbtn", "Fullscreen", "fs");
    on(this._fsBtn, "click", () => this._toggleFullscreen());

    this._dlBtn = null;
    if (this.downloadAllowed) {
      this._dlBtn = iconBtn("rtr-dlbtn", "Download", "play");
      setIcon(this._dlBtn, "dl");
      on(this._dlBtn, "click", () => this._startDownload());
    }

    const barItems = [this._playBtn, this._volBtn, prog, this._time, this._qual, this._speedBtn, this._moreBtn];
    if (this._dlBtn) barItems.push(this._dlBtn);
    barItems.push(this._fsBtn);
    bar.append(...barItems);

    const volPop = add(el("div", "rtr-pop"));
    if (this._volSupported) volPop.appendChild(this._makeVolumeWidget());
    const speedPop = add(el("div", "rtr-pop"));
    speedPop.appendChild(this._makeSpeedList());
    const morePop = add(el("div", "rtr-pop"));
    if (this._volSupported) morePop.appendChild(this._makeVolumeWidget());
    const lbl = el("div", "rtr-label");
    lbl.textContent = "Speed";
    morePop.append(lbl, this._makeSpeedList());
    this._qrow = el("div", "rtr-qrow");
    this._qrow.hidden = true;
    morePop.appendChild(this._qrow);
    this._registerPop(volPop, this._volBtn);
    this._registerPop(speedPop, this._speedBtn);
    this._registerPop(morePop, this._moreBtn);

    this._fetchLine = add(el("div", "rtr-fetch", { "aria-hidden": "true" }));

    const hold = (this._holdMsg = add(
      el("div", "rtr-msg rtr-hold-msg", { role: "alertdialog", "aria-label": "Playback paused" })
    ));
    hold.appendChild(icon("pause"));
    this._holdText = el("p", "rtr-msg-t");
    this._holdBtn = el("button", "rtr-cta", { type: "button" });
    this._holdBtn.textContent = "Resume";
    on(this._holdBtn, "click", () => this._releaseHold(null, true));
    hold.append(this._holdText, this._holdBtn);

    const err = (this._errMsg = add(el("div", "rtr-msg rtr-err-msg", { role: "alert" })));
    err.appendChild(icon("warn"));
    this._errText = el("p", "rtr-msg-t");
    this._errCode = el("span", "rtr-code");
    this._errBtn = el("button", "rtr-cta", { type: "button" });
    this._errBtn.textContent = "Retry";
    on(this._errBtn, "click", () => this._onRetryClick());
    err.append(this._errText, this._errCode, this._errBtn);

    on(this._volBtn, "click", () => this._togglePop(volPop, this._volBtn));
    on(this._speedBtn, "click", () => this._togglePop(speedPop, this._speedBtn));
    on(this._moreBtn, "click", () => this._togglePop(morePop, this._moreBtn));
    on(document, "pointerdown", (e) => {
      if (!this._popOpen()) return;
      if (this._pops.some((p) => p.el.contains(e.target) || p.anchor.contains(e.target))) return;
      this._closePops();
    });

    on(root, "pointermove", (e) => { if (e.pointerType === "mouse") this._poke(); });
    on(root, "pointerdown", (e) => {
      this._kbd = false;
      this._lastPointerType = e.pointerType || "mouse";
      this._poke();
      const t = e.target;
      const interactive = t.closest && t.closest(".rtr-controls,.rtr-pop,.rtr-msg");
      if (!interactive) root.focus({ preventScroll: true });
    });
    on(root, "focusin", () => this._poke());
    on(document, "keydown", (e) => this._onKeyDown(e));
    on(document, "fullscreenchange", () => this._onFullscreenChange());
    on(document, "webkitfullscreenchange", () => this._onFullscreenChange());

    on(v, "play", () => {
      if (this._holdReasons.size) { v.pause(); return; }
      this.security?.setPlaying(true);
      this._syncPlayIcon();
      this._poke();
      this._startTick();
    });
    on(v, "playing", () => { this._setLoading(false); this._startTick(); });
    on(v, "pause", () => { this.security?.setPlaying(false); this._syncPlayIcon(); this._poke(); });
    on(v, "ended", () => {
      this.security?.setPlaying(false);
      this._beat("progress");
      this.onEnded();
      this._syncPlayIcon();
      this._syncUI();
      this._announce("Playback ended");
    });
    on(v, "waiting", () => this._setLoading(true));
    on(v, "canplay", () => this._setLoading(false));
    on(v, "timeupdate", () => this._syncUI());
    on(v, "progress", () => this._syncUI());
    on(v, "durationchange", () => this._syncUI());
    on(v, "volumechange", () => this._syncVolume());
    on(v, "ratechange", () => this._syncRate());
    on(v, "seeking", () => this._handleSeeking());
    on(v, "error", () => this._onVideoError());
    on(document, "visibilitychange", () => { if (!document.hidden) this._startTick(); });

    if (typeof ResizeObserver !== "undefined") {
      this._ro = new ResizeObserver((entries) => this._applySize(entries[0].contentRect.width));
      this._ro.observe(root);
    } else {
      on(window, "resize", () => this._applySize(root.clientWidth));
    }
    this._applySize(root.clientWidth);

    this._restoreVolume();
    this._syncVolume();
    this._syncRate();
    this._syncPlayIcon();
    this._syncUI();
  }

  _applySize(w) {
    if (!(w > 0)) return;
    const r = this.container.classList;
    r.toggle("rtr-compact", w < COMPACT_PX);
    r.toggle("rtr-tiny", w < TINY_PX);
    this._closePops();
  }

  _wireProgress(prog, range) {
    range.addEventListener("pointerdown", () => {
      this._scrubbing = true;
      this.container.classList.add("rtr-scrub");
      this._poke();
    });
    const endScrub = () => {
      this._scrubbing = false;
      this.container.classList.remove("rtr-scrub");
    };
    window.addEventListener("pointerup", endScrub);
    window.addEventListener("pointercancel", endScrub);
    this._cleanup.push(() => {
      window.removeEventListener("pointerup", endScrub);
      window.removeEventListener("pointercancel", endScrub);
    });

    range.addEventListener("input", () => {
      const dur = this._duration();
      const p = Number(range.value) / 1000;
      this._prog.style.setProperty("--rtr-p", p.toFixed(4));
      this._timeTxt = `${fmt(p * dur)} / ${fmt(dur)}`;
      this._time.textContent = this._timeTxt;
    });
    range.addEventListener("change", () => {
      const dur = this._duration();
      if (!(dur > 0)) return;
      try { this.video.currentTime = (Number(range.value) / 1000) * dur; } catch {}
    });
    prog.addEventListener("pointermove", (e) => {
      const r = prog.getBoundingClientRect();
      if (!(r.width > 0)) return;
      const x = clamp(e.clientX - r.left, 0, r.width);
      this._tip.textContent = fmt((x / r.width) * this._duration());
      const half = this._tip.offsetWidth / 2;
      this._tip.style.left = `${clamp(x, half, Math.max(half, r.width - half))}px`;
    });
  }

  _registerPop(popEl, anchor) {
    popEl.addEventListener("keydown", (e) => {
      if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); this._closePops(true); }
      else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const items = [...popEl.querySelectorAll(".rtr-item")];
        if (!items.length) return;
        e.preventDefault(); e.stopPropagation();
        const i = items.indexOf(document.activeElement);
        items[(i + (e.key === "ArrowDown" ? 1 : -1) + items.length) % items.length].focus();
      }
    });
    this._pops.push({ el: popEl, anchor });
  }

  _popOpen() { return this._pops.some((p) => p.el.classList.contains("rtr-open")); }

  _closePops(refocus) {
    let anchor = null;
    for (const p of this._pops) {
      if (p.el.classList.contains("rtr-open")) {
        p.el.classList.remove("rtr-open");
        p.anchor.setAttribute("aria-expanded", "false");
        anchor = p.anchor;
      }
    }
    if (refocus && anchor) anchor.focus({ preventScroll: true });
  }

  _togglePop(popEl, anchor) {
    const wasOpen = popEl.classList.contains("rtr-open");
    this._closePops();
    if (wasOpen) return;
    popEl.style.visibility = "hidden";
    popEl.classList.add("rtr-open");
    const c = this.container.getBoundingClientRect();
    const a = anchor.getBoundingClientRect();
    const w = popEl.offsetWidth;
    const left = clamp(a.left + a.width / 2 - w / 2 - c.left, 8, Math.max(8, c.width - w - 8));
    popEl.style.left = `${left}px`;
    popEl.style.bottom = `${Math.max(8, c.bottom - a.top + 6)}px`;
    popEl.style.visibility = "";
    anchor.setAttribute("aria-expanded", "true");
    this._poke();
    const target = popEl.querySelector('.rtr-item[aria-checked="true"]') || popEl.querySelector("input,button");
    if (target) target.focus({ preventScroll: true });
  }

  _detectVolumeSupport() {
    try {
      const v = this.video;
      const o = v.volume;
      v.volume = o > 0.5 ? 0.25 : 0.75;
      const ok = Math.abs(v.volume - o) > 0.01;
      v.volume = o;
      return ok;
    } catch { return false; }
  }

  _makeVolumeWidget() {
    const row = el("div", "rtr-volrow");
    const mute = iconBtn("rtr-vmute", "Mute", "volHigh");
    const r = el("input", "rtr-volrange", { type: "range", min: "0", max: "100", step: "1", "aria-label": "Volume" });
    mute.addEventListener("click", () => this._toggleMute());
    r.addEventListener("input", () => this._setVolume(Number(r.value) / 100));
    row.append(mute, r);
    this._volWidgets.push({ mute, range: r });
    return row;
  }

  _makeSpeedList() {
    const list = el("div", "rtr-list", { role: "menu", "aria-label": "Playback speed" });
    for (const s of SPEEDS) {
      const b = el("button", "rtr-item", { type: "button", role: "menuitemradio", "aria-checked": "false" });
      b.dataset.rate = String(s);
      b.textContent = `${s}\u00d7`;
      b.addEventListener("click", () => { this.video.playbackRate = s; this._closePops(true); });
      list.appendChild(b);
      this._speedItems.push(b);
    }
    return list;
  }

  _restoreVolume() {
    if (!this._volSupported) return;
    try {
      const saved = JSON.parse(sessionStorage.getItem(VOL_KEY) || "null");
      if (saved && Number.isFinite(saved.v)) {
        this.video.volume = clamp(saved.v, 0, 1);
        this.video.muted = !!saved.m;
      }
    } catch {}
  }

  _persistVolume() {
    try { sessionStorage.setItem(VOL_KEY, JSON.stringify({ v: this.video.volume, m: this.video.muted })); } catch {}
  }

  _setVolume(v) {
    const n = clamp(v, 0, 1);
    this.video.volume = n;
    if (n > 0 && this.video.muted) this.video.muted = false;
  }

  _toggleMute() {
    const v = this.video;
    if (v.muted || v.volume === 0) { v.muted = false; if (v.volume === 0) v.volume = 0.5; }
    else v.muted = true;
  }

  _syncVolume() {
    const v = this.video;
    const level = v.muted ? 0 : v.volume;
    const name = level === 0 ? "volMuted" : level < 0.5 ? "volLow" : "volHigh";
    const pct = Math.round(level * 100);
    setIcon(this._volBtn, name);
    for (const w of this._volWidgets) {
      setIcon(w.mute, name);
      const label = level === 0 ? "Unmute" : "Mute";
      w.mute.setAttribute("aria-label", label);
      w.mute.title = label;
      w.range.value = String(pct);
      w.range.style.setProperty("--rtr-v", `${pct}%`);
      w.range.setAttribute("aria-valuetext", `${pct}%`);
    }
    this._volBtn.setAttribute("aria-label", `Volume, ${pct}%`);
    this._persistVolume();
  }

  _syncRate() {
    const r = this.video.playbackRate;
    this._speedBtn.textContent = `${r}\u00d7`;
    for (const b of this._speedItems) b.setAttribute("aria-checked", String(Number(b.dataset.rate) === r));
  }

  _toast(text) {
    this._toastEl.textContent = text;
    this._toastEl.classList.add("rtr-show");
    clearTimeout(this._toastTimer);
    this._toastTimer = setTimeout(() => this._toastEl.classList.remove("rtr-show"), 1100);
  }

  _announce(text) { if (this._live) this._live.textContent = text; }

  _togglePlay() {
    if (this._holdReasons.size || this._failed) return;
    if (this.video.paused || this.video.ended) this.play();
    else this.video.pause();
  }

  _seekBy(delta, quiet) {
    const dur = this._duration();
    let t = (this.video.currentTime || 0) + delta;
    t = Math.max(0, dur > 0 ? Math.min(t, dur - 0.05) : t);
    try { this.video.currentTime = t; } catch {}
    if (!quiet) this._toast(`${delta > 0 ? "+" : "\u2212"}${Math.abs(delta)} s`);
    this._syncUI();
  }

  _isFullscreen() {
    const fs = document.fullscreenElement || document.webkitFullscreenElement;
    return fs === this.container || this._pfs;
  }

  _toggleFullscreen() {
    if (this._pfs) return this._setPseudoFs(false);
    const doc = document;
    const cur = doc.fullscreenElement || doc.webkitFullscreenElement;
    if (cur === this.container) {
      try { (doc.exitFullscreen || doc.webkitExitFullscreen)?.call(doc); } catch {}
      return;
    }
    const req = this.container.requestFullscreen || this.container.webkitRequestFullscreen;
    if (!req) return this._setPseudoFs(true);
    try {
      const p = req.call(this.container);
      if (p && p.catch) p.catch(() => this._setPseudoFs(true));
    } catch { this._setPseudoFs(true); }
  }

  async _startDownload() {
    if (!this.downloadAllowed || this._downloading || this._destroyed) return;
    const c = this.client;
    if (!c.baseUrl || !c.apiKey) { this._toast("Downloads are not available."); return; }
    const headers = { Authorization: `Bearer ${c.apiKey}`, "Content-Type": "application/json" };

    this._downloading = true;
    if (this._dlBtn) this._dlBtn.disabled = true;
    this._toast("Preparing download...");
    try {
      let r;
      try {
        r = await fetch(`${c.baseUrl}/sdk/download-request`, {
          method: "POST", headers, body: JSON.stringify({ session_id: this.sessionId }),
        });
      } catch (netErr) {
        // TypeError "Failed to fetch": the server was unreachable, restarted, or blocked by CORS.
        console.error("[RightraPlayer] download-request could not reach the server:", c.baseUrl, netErr);
        this._toast("Cannot reach the server.");
        return;
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.job_id) throw new Error(j.message || `HTTP ${r.status}`);

      const deadline = Date.now() + 30 * 60 * 1000;
      let netFails = 0;
      for (;;) {
        await sleep(2000);
        if (this._destroyed) return;
        if (Date.now() > deadline) throw new Error("The download took too long.");
        let s;
        try {
          const sr = await fetch(`${c.baseUrl}/sdk/download-status/${j.job_id}`, { headers });
          s = await sr.json();
          if (sr.status === 404) throw new Error("The download was lost (the server restarted). Please try again.");
          netFails = 0;
        } catch (e) {
          if (e instanceof TypeError && ++netFails < 5) continue; // transient network error: keep polling
          throw e;
        }
        if (s.status === "ready") break;
        if (s.status === "failed" || s.status === "expired" || s.success === false) throw new Error(s.message || "failed");
        this._toast(`Preparing download... ${Math.round((s.progress || 0) * 100)}%`);
      }

      const f = await fetch(`${c.baseUrl}/sdk/download-file/${j.job_id}`, { headers });
      if (!f.ok) throw new Error(`HTTP ${f.status}`);
      const url = URL.createObjectURL(await f.blob());
      const a = document.createElement("a");
      a.href = url; a.download = `${this.contentId}.mp4`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
      this._toast("Download started.");
    } catch (err) {
      console.error("[RightraPlayer] download failed:", err);
      this._toast("Download failed.");
    } finally {
      this._downloading = false;
      if (this._dlBtn) this._dlBtn.disabled = false;
    }
  }

  _setPseudoFs(on) {
    this._pfs = on;
    this.container.classList.toggle("rtr-pfs", on);
    document.documentElement.classList.toggle("rtr-noscroll", on);
    this._onFullscreenChange();
  }

  _onFullscreenChange() {
    const fs = this._isFullscreen();
    this.container.classList.toggle("rtr-fs", fs);
    setIcon(this._fsBtn, fs ? "fsExit" : "fs");
    const label = fs ? "Exit fullscreen" : "Fullscreen";
    this._fsBtn.setAttribute("aria-label", label);
    this._fsBtn.title = label;
    this._closePops();
  }

  _syncPlayIcon() {
    const v = this.video;
    const playing = !v.paused && !v.ended;
    const name = playing ? "pause" : v.ended ? "replay" : "play";
    const label = playing ? "Pause" : v.ended ? "Replay" : "Play";
    setIcon(this._playBtn, name);
    setIcon(this._big, name);
    this._playBtn.setAttribute("aria-label", label);
    this._playBtn.title = label;
    this._big.setAttribute("aria-label", label);
    this.container.classList.toggle("rtr-paused", !playing);
  }

  _duration() {
    const d = this.video.duration;
    if (Number.isFinite(d) && d > 0) return d;
    const m = Number(this.manifest?.duration);
    return Number.isFinite(m) && m > 0 ? m : 0;
  }

  _bufferedEnd() {
    const t = this.video.currentTime;
    const b = this.video.buffered;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) <= t + 0.25 && b.end(i) > t) return b.end(i);
    }
    return 0;
  }

  _syncUI() {
    if (this._destroyed || !this._range) return;
    const dur = this._duration();
    const cur = Math.min(this.video.currentTime || 0, dur || Infinity);
    const p = dur > 0 ? Math.min(1, cur / dur) : 0;
    const b = dur > 0 ? Math.min(1, this._bufferedEnd() / dur) : 0;
    if (!this._scrubbing) {
      this._prog.style.setProperty("--rtr-p", p.toFixed(4));
      this._range.value = String(Math.round(p * 1000));
      const txt = `${fmt(cur)} / ${fmt(dur)}`;
      if (txt !== this._timeTxt) {
        this._timeTxt = txt;
        this._time.textContent = txt;
        this._range.setAttribute("aria-valuetext", `${fmt(cur)} of ${fmt(dur)}`);
      }
    }
    this._prog.style.setProperty("--rtr-b", b.toFixed(4));
    this._range.disabled = !(dur > 0);
  }

  _tick = (ts) => {
    this._raf = 0;
    if (this._destroyed) return;
    if (ts - this._lastUi >= 100) {
      this._lastUi = ts;
      this._syncUI();
    }
    if (!this.video.paused && !this.video.ended && !document.hidden) {
      this._raf = requestAnimationFrame(this._tick);
    }
  };

  _startTick() {
    if (!this._raf && typeof requestAnimationFrame === "function") this._raf = requestAnimationFrame(this._tick);
  }

  _setLoading(on) { this.container.classList.toggle("rtr-loading", !!on && !this._failed); }

  _setFetching(delta) {
    this._fetchDepth = Math.max(0, this._fetchDepth + delta);
    this.container.classList.toggle("rtr-fetching", this._fetchDepth > 0);
  }

  _poke() {
    if (this._destroyed) return;
    this.container.classList.remove("rtr-idle");
    clearTimeout(this._idleTimer);
    this._idleTimer = setTimeout(() => this._maybeIdle(), IDLE_MS);
  }

  _maybeIdle() {
    if (this._destroyed || this.video.paused || this.video.ended) return;
    const busy =
      this._popOpen() || this._scrubbing || this._holdReasons.size || this._failed ||
      (this._kbd && this.controls.contains(document.activeElement));
    if (busy) { this._idleTimer = setTimeout(() => this._maybeIdle(), 1000); return; }
    this.container.classList.add("rtr-idle");
  }

  _toggleControls() {
    const c = this.container.classList;
    if (c.contains("rtr-idle")) this._poke();
    else if (!this.video.paused) { this._closePops(); c.add("rtr-idle"); }
  }

  _onGesture(e) {
    if (this._holdReasons.size || this._failed) return;
    this._lastPointerType = e.pointerType || "mouse";
    this._closePops();
    if (e.pointerType !== "touch" && e.pointerType !== "pen") {
      if (e.button === 0) this._togglePlay();
      return;
    }
    const rect = this.gesture.getBoundingClientRect();
    const x = rect.width > 0 ? (e.clientX - rect.left) / rect.width : 0.5;
    const side = x < 1 / 3 ? -1 : x > 2 / 3 ? 1 : 0;
    const now = performance.now();

    if (this._chain && now < this._chain.until && side === this._chain.side && side !== 0) {
      this._doubleSeek(side);
      return;
    }
    const last = this._lastTap;
    if (last && now - last.t < 300 && Math.abs(e.clientX - last.x) < 70) {
      clearTimeout(this._tapTimer);
      this._lastTap = null;
      if (side === 0) this._togglePlay();
      else this._doubleSeek(side);
      return;
    }
    this._lastTap = { t: now, x: e.clientX };
    clearTimeout(this._tapTimer);
    this._tapTimer = setTimeout(() => { this._lastTap = null; this._toggleControls(); }, 300);
  }

  _doubleSeek(side) {
    this._seekBy(side * SEEK_TAP_S, true);
    this._chain = { side, until: performance.now() + 600 };
    const rip = side < 0 ? this._ripL : this._ripR;
    rip.classList.remove("rtr-go");
    void rip.offsetWidth;
    rip.classList.add("rtr-go");
    this._poke();
  }

  _onKeyDown(e) {
    if (this._destroyed) return;
    const inside = this.container.contains(e.target);
    if (!inside && !this._isFullscreen()) return;
    this._kbd = true;
    this._poke();
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key === "Escape") {
      if (this._popOpen()) { e.preventDefault(); this._closePops(true); }
      else if (this._pfs) { e.preventDefault(); this._setPseudoFs(false); }
      return;
    }
    if (this._holdReasons.size || this._failed) return;

    const t = e.target;
    const isVolRange = !!(t && t.classList && t.classList.contains("rtr-volrange"));
    if (t && t.closest && t.closest(".rtr-pop") && !isVolRange) return;
    const isButton = !!(t && t.tagName === "BUTTON");

    switch (e.key) {
      case " ":
      case "Spacebar":
        if (isButton) return;
        e.preventDefault();
        this._togglePlay();
        break;
      case "ArrowLeft":
      case "ArrowRight":
        if (isVolRange) return;
        e.preventDefault();
        this._seekBy(e.key === "ArrowLeft" ? -SEEK_KEY_S : SEEK_KEY_S);
        break;
      case "ArrowUp":
      case "ArrowDown":
        if (isVolRange) return;
        e.preventDefault();
        if (!this._volSupported) return;
        this._setVolume((this.video.muted ? 0 : this.video.volume) + (e.key === "ArrowUp" ? 0.05 : -0.05));
        this._toast(`Volume ${Math.round(this.video.volume * 100)}%`);
        break;
      case "f":
      case "F":
        e.preventDefault();
        this._toggleFullscreen();
        break;
      case "m":
      case "M":
        e.preventDefault();
        this._toggleMute();
        this._toast(this.video.muted || this.video.volume === 0 ? "Muted" : "Unmuted");
        break;
      default:
    }
  }

  _showError(err) {
    this._lastError = err;
    const code = err.code in FRIENDLY ? err.code : "UNKNOWN";
    this._errText.textContent = FRIENDLY[code];
    this._errCode.textContent = code;
    const canRetry = code !== "UNSUPPORTED" && (code !== "SESSION_EXPIRED" || !!this.onRetry);
    this._errBtn.hidden = !canRetry;
    this._closePops();
    this.container.classList.remove("rtr-loading", "rtr-idle");
    this.container.classList.add("rtr-error");
    this._announce(`Playback error. ${FRIENDLY[code]}`);
    if (canRetry) this._errBtn.focus({ preventScroll: true });
  }

  _hideError() { this.container.classList.remove("rtr-error"); }

  _onRetryClick() {
    if (this._lastError?.code === "SESSION_EXPIRED" && this.onRetry) { this.onRetry(this._lastError); return; }
    this.retry();
  }

  _holdMessage() {
    return this._holdReasons.has("devtools")
      ? "Playback paused. Close developer tools to continue."
      : "Playback paused because this tab was in the background.";
  }

  _holdPlayback(reason) {
    if (this._destroyed) return;
    if (!this._holdReasons.size) this._wasPlaying = !this.video.paused && !this.video.ended;
    this._holdReasons.add(reason);
    try { this.video.pause(); } catch {}
    this._holdText.textContent = this._holdMessage();
    this._closePops();
    this.container.classList.add("rtr-hold");
    this.container.classList.remove("rtr-idle");
    this._announce(this._holdText.textContent);
    this._holdBtn.focus({ preventScroll: true });
  }

  _releaseHold(reason, byUser) {
    if (!this._holdReasons.size) return;
    if (reason) this._holdReasons.delete(reason);
    else this._holdReasons.clear();
    if (this._holdReasons.size) { this._holdText.textContent = this._holdMessage(); return; }
    this.container.classList.remove("rtr-hold");
    const resume = this._wasPlaying || !!byUser;
    this._wasPlaying = false;
    this.container.focus({ preventScroll: true });
    if (resume) this.video.play().catch(() => {});
  }

  _onSecurityEvent(type) {
    if (this._destroyed) return;
    const sec = this.securityOptions;
    switch (type) {
      case "page_closing": this._sendEndedKeepalive(); return;
      case "devtools_open":
        this._reportDownloadAttempt(type);
        if (sec.devtools === "pause") this._holdPlayback("devtools");
        return;
      case "devtools_close": this._releaseHold("devtools"); return;
      case "tab_hidden": return;
      case "tab_hidden_threshold":
        this._reportDownloadAttempt("tab_hidden_long");
        if (sec.tabSwitch === "pause") this._holdPlayback("tab");
        return;
      case "tab_visible": this._releaseHold("tab"); return;
      default:
        this._reportDownloadAttempt(type);
    }
  }

  _sendEndedKeepalive() {
    if (this._endedSent || !this.sessionId) return;
    this._endedSent = true;
    const payload = {
      session_id: this.sessionId,
      event: "ended",
      seconds_watched: Math.floor(this.video.currentTime || 0),
    };
    try {
      const c = this.client;
      if (c.baseUrl && c.apiKey && typeof fetch === "function") {
        fetch(`${c.baseUrl}/sdk/playback-event`, {
          method: "POST",
          keepalive: true,
          headers: { Authorization: `Bearer ${c.apiKey}`, "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }).catch(() => {});
        return;
      }
    } catch {}
    this.client.sendPlaybackEvent(payload).catch(() => {});
  }

  async load() {
    const MS = getMediaSourceCtor();
    if (!MS) {
      throw new RightraPlaybackError(
        "UNSUPPORTED",
        "This browser does not support Media Source Extensions, which protected playback requires."
      );
    }

    try {
      this.key = await importAesKey(this.keyBytes, ["decrypt"]);
    } catch (e) {
      throw new RightraPlaybackError("DECRYPT_FAILED", "The playback key could not be loaded.", e);
    }
    this.keyBytes.fill(0);
    this.keyBytes = null;

    await this._loadManifest();
    if (this._destroyed) return;

    const mime = this.manifest.mime || 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"';
    if (!MS.isTypeSupported(mime)) {
      throw new RightraPlaybackError("UNSUPPORTED", `This browser cannot play the required format: ${mime}`);
    }

    await this._openMedia();
    if (this._destroyed) return;

    if (this.sessionCode === null) {
      throw new RightraPlaybackError("WATERMARK_FAILED", "No session watermark code.");
    }

    // Invisible layers are ALWAYS on and fail closed. The old code swallowed
    // errors (`catch { this.videoWatermark = null }`) and played unmarked video.
    try {
      this.audioWatermark = new AudioWatermark(this.sessionCode);
      this.audioWatermark.attach(this.video);

      this.videoWatermark = new VideoWatermark(this.container, this.video, this.sessionCode, {
        onFail: (e) => this._fail(new RightraPlaybackError("WATERMARK_FAILED", "Watermark render failed.", e)),
      });
      this.videoWatermark.attach();
    } catch (e) {
      throw new RightraPlaybackError("WATERMARK_FAILED", "Could not start the watermark.", e);
    }

    const payload = this.watermarkPayload ? base64ToBytes(this.watermarkPayload) : new Uint8Array(0);
    if (this.watermarkOptions.visual) {
      this.visualWatermark = new VisualWatermark(this.container, payload, {
        onTamper: (what) => { this.video.pause(); this._reportDownloadAttempt(what); },
      });
      this.visualWatermark.attach();
    }
    payload.fill(0);
    this.watermarkPayload = null;

    this._loaded = true;
    this._heartbeat = setInterval(() => this._beat("heartbeat"), HEARTBEAT_MS);
    this._beat("start");
    this._startLoader(0);
    this._syncPlayIcon();
    this._syncUI();
  }

  async _loadManifest() {
    const bytes = await fetchBinary(this.manifestUrl);
    try {
      this.manifest = JSON.parse(new TextDecoder().decode(bytes));
    } catch (e) {
      throw new RightraPlaybackError("MANIFEST_INVALID", "The manifest is not valid JSON.", e);
    }
    const m = this.manifest;
    if (!m || !Array.isArray(m.segments) || m.segments.length === 0) {
      throw new RightraPlaybackError("MANIFEST_INVALID", "The manifest has no segments.");
    }
    this.version = Number(m.version) || 1;
    const segs = m.segments.map((s, i) => ({ ...s, index: s.index ?? i }));
    this.initSeg = segs.find((s) => s.init) || null;
    this.mediaSegs = segs.filter((s) => !s.init);
    this.timeIndexed =
      !!m.timeIndexed && this.mediaSegs.length > 0 && this.mediaSegs.every((s) => Number.isFinite(s.startTime));

    const h = Number(m.height);
    if (h > 0) {
      this._qual.textContent = h >= 2160 ? "4K" : `${h}p`;
      this._qual.hidden = false;
      this._qrow.textContent = `Quality: ${this._qual.textContent} (source)`;
      this._qrow.hidden = false;
    }
  }

  async _openMedia() {
    const MS = getMediaSourceCtor();
    const mime = this.manifest.mime || 'video/mp4; codecs="avc1.42E01E,mp4a.40.2"';
    this._managed = !window.MediaSource && !!window.ManagedMediaSource;

    try {
      this.mediaSource = new MS();
    } catch (e) {
      throw new RightraPlaybackError("UNSUPPORTED", "The browser could not start its video engine.", e);
    }
    this._objectUrl = URL.createObjectURL(this.mediaSource);
    this.video.src = this._objectUrl;

    await new Promise((resolve, reject) => {
      const t = setTimeout(() => {
        if (this._destroyed) resolve();
        else reject(new RightraPlaybackError("MEDIA_ERROR", "The video engine did not start in time."));
      }, 10000);
      this.mediaSource.addEventListener("sourceopen", () => { clearTimeout(t); resolve(); }, { once: true });
    });
    if (this._destroyed) return;

    try {
      this.sourceBuffer = this.mediaSource.addSourceBuffer(mime);
    } catch (e) {
      throw new RightraPlaybackError("UNSUPPORTED", `The browser rejected the stream format: ${mime}`, e);
    }
    this.sourceBuffer.mode = "segments";
    this.sourceBuffer.addEventListener("updateend", () => { for (const w of [...this._waiters]) w(); });
    this.sourceBuffer.addEventListener("error", () => {
      this._sbError = new RightraPlaybackError("MEDIA_ERROR", "The video buffer rejected the data (codec or container mismatch).");
    });
    const d = this.manifest.duration;
    if (d > 0 && Number.isFinite(d)) {
      try { this.mediaSource.duration = d; } catch {}
    }
  }

  async retry() {
    if (this._destroyed || this._retrying) return;
    this._retrying = true;
    try {
      this._hideError();
      this._failed = false;
      this._setLoading(true);
      const resumeAt = this.video.currentTime || 0;

      this._gen++;
      this._abort?.abort();
      this._resetting = true;
      for (const w of [...this._waiters]) w();
      try {
        this.video.pause();
        this.video.removeAttribute("src");
        this.video.load();
      } catch {}
      if (this._objectUrl) URL.revokeObjectURL(this._objectUrl);
      this._objectUrl = null;
      this.mediaSource = null;
      this.sourceBuffer = null;
      this._sbError = null;
      this._initDone = false;
      this._appended = 0;
      this._appendChain = Promise.resolve();
      this._resetting = false;

      if (!this.manifest) await this._loadManifest();
      await this._openMedia();
      if (this._destroyed) return;

      let idx = 0;
      if (this.timeIndexed) {
        for (let i = 0; i < this.mediaSegs.length; i++) {
          if (this.mediaSegs[i].startTime <= resumeAt) idx = i;
          else break;
        }
      }
      this._resumeAt = resumeAt;
      if (!this._heartbeat) this._heartbeat = setInterval(() => this._beat("heartbeat"), HEARTBEAT_MS);
      this._startLoader(idx);
    } catch (e) {
      this._resetting = false;
      this._fail(e);
    } finally {
      this._retrying = false;
    }
  }

  _alive(gen) { return !this._destroyed && !this._failed && gen === this._gen; }

  _startLoader(fromIndex) {
    this._gen++;
    const gen = this._gen;
    this._abort?.abort();
    this._abort = new AbortController();
    this._next = fromIndex;
    this._runLoader(gen, this._abort.signal).catch((err) => {
      if (this._alive(gen) && err?.name !== "AbortError") this._fail(err);
    });
  }

  async _runLoader(gen, signal) {
    if (!this._initDone && this.initSeg) {
      const bytes = await this._loadSegment(this.initSeg, signal);
      if (!this._alive(gen)) return;
      await this._append(bytes);
      this._initDone = true;
    }
    if (this._resumeAt !== null) {
      const t = this._resumeAt;
      this._resumeAt = null;
      if (t > 0.5) {
        this._skipSeek = true;
        try { this.video.currentTime = t; } catch { this._skipSeek = false; }
      }
    }

    while (this._alive(gen)) {
      if (this._next >= this.mediaSegs.length) break;
      if (this._managed && this._appended >= 2 && this.mediaSource.streaming === false) {
        await sleep(300, signal);
        continue;
      }
      if (this._bufferedAhead() >= BUFFER_AHEAD_S) { await sleep(400, signal); continue; }
      if (this._isSegmentBuffered(this._next)) { this._next++; continue; }
      this._setFetching(1);
      let bytes;
      try {
        bytes = await this._loadSegment(this.mediaSegs[this._next], signal);
      } finally {
        this._setFetching(-1);
      }
      if (!this._alive(gen)) return;
      await this._append(bytes);
      if (!this._alive(gen)) return;
      this._next++;
    }

    if (this._alive(gen)) {
      await this._appendChain.catch(() => {});
      this._maybeEnd();
    }
  }

  async _loadSegment(seg, signal) {
    const url = resolveSegmentUrl(this.manifestUrl, seg);
    const encrypted = await fetchBinary(url, signal);

    if (this.version >= 2 && seg.size && encrypted.byteLength !== seg.size) {
      throw new RightraPlaybackError(
        "SEGMENT_MISMATCH",
        `Segment ${seg.index} is ${encrypted.byteLength} bytes but the manifest expects ${seg.size}. ` +
        "Storage may be serving a truncated file, an error page, or an older upload. Re-register the work."
      );
    }
    if (encrypted.byteLength < 17) {
      throw new RightraPlaybackError("SEGMENT_MISMATCH", `Segment ${seg.index} is too small to be valid.`);
    }

    try {
      return await decryptSegmentBuffer(
        this.key,
        encrypted,
        base64ToBytes(seg.iv),
        this.version >= 2 ? segmentAad(this.contentId, seg.index) : undefined
      );
    } catch (e) {
      throw new RightraPlaybackError(
        "DECRYPT_FAILED",
        `Segment ${seg.index} failed authentication: wrong key, wrong IV, or the data was altered or came from a different upload.`,
        e
      );
    }
  }

  _waitIdle() {
    const sb = this.sourceBuffer;
    if (!sb || !sb.updating) return Promise.resolve();
    return new Promise((resolve) => {
      const done = () => {
        sb.removeEventListener("updateend", done);
        this._waiters.delete(done);
        resolve();
      };
      this._waiters.add(done);
      sb.addEventListener("updateend", done);
    });
  }

  _append(bytes) {
    const p = this._appendChain.catch(() => {}).then(() => this._appendNow(bytes));
    this._appendChain = p;
    return p;
  }

  async _appendNow(bytes) {
    for (let attempt = 0; attempt < 5; attempt++) {
      if (this._destroyed || !this.mediaSource || this.mediaSource.readyState === "closed") return;
      await this._waitIdle();
      try {
        await this._evictBehind(false);
        this.sourceBuffer.appendBuffer(bytes);
        await this._waitIdle();
        if (this._sbError) throw this._sbError;
        this._appended++;
        return;
      } catch (e) {
        if (e?.name === "QuotaExceededError") {
          await this._evictBehind(true);
          await sleep(1000);
          continue;
        }
        if (e instanceof RightraPlaybackError) throw e;
        throw new RightraPlaybackError("MEDIA_ERROR", "Appending video data failed.", e);
      }
    }
    throw new RightraPlaybackError("BUFFER_FULL", "The browser's video buffer is full.");
  }

  async _evictBehind(force) {
    const b = this.video.buffered;
    if (!b.length || !this.sourceBuffer) return;
    const cut = this.video.currentTime - (force ? 5 : KEEP_BEHIND_S);
    if (cut - b.start(0) < (force ? 1 : 30)) return;
    await this._waitIdle();
    try {
      this.sourceBuffer.remove(b.start(0), cut);
      await this._waitIdle();
    } catch {}
  }

  _bufferedAhead() {
    const t = this.video.currentTime;
    const b = this.video.buffered;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) <= t + 0.25 && b.end(i) > t) return b.end(i) - t;
    }
    return 0;
  }

  _isRangeBuffered(from, to) {
    const b = this.video.buffered;
    for (let i = 0; i < b.length; i++) {
      if (b.start(i) <= from && b.end(i) >= to) return true;
    }
    return false;
  }

  _isSegmentBuffered(idx) {
    if (!this.timeIndexed) return false;
    const next = this.mediaSegs[idx + 1];
    if (!next) return false;
    return this._isRangeBuffered(this.mediaSegs[idx].startTime + 0.1, next.startTime - 0.1);
  }

  _maybeEnd() {
    if (this._destroyed || !this.mediaSource || !this.sourceBuffer) return;
    if (this.mediaSource.readyState === "open" && !this.sourceBuffer.updating) {
      try { this.mediaSource.endOfStream(); } catch {}
    }
  }

  _handleSeeking() {
    if (this._skipSeek) { this._skipSeek = false; return; }
    if (!this.timeIndexed || this._destroyed || this._failed || !this._loaded) return;
    const t = this.video.currentTime;
    if (this._isRangeBuffered(t, t + 0.1)) return;
    let idx = 0;
    for (let i = 0; i < this.mediaSegs.length; i++) {
      if (this.mediaSegs[i].startTime <= t) idx = i;
      else break;
    }
    this._startLoader(idx);
  }

  _onVideoError() {
    if (this._resetting || this._failed || this._destroyed) return;
    const code = this.video.error?.code;
    this._fail(
      new RightraPlaybackError(
        "MEDIA_ERROR",
        `The browser could not decode the video (media error ${code ?? "?"}). The file's codecs may not match the manifest.`
      )
    );
  }

  _beat(event) {
    if (this._destroyed || !this.sessionId) return Promise.resolve();
    return this.client
      .sendPlaybackEvent({
        session_id: this.sessionId,
        event,
        seconds_watched: Math.floor(this.video.currentTime || 0),
      })
      .catch((err) => {
        if (event === "ended") return;
        if (err?.status === 410 || err?.code === "SESSION_EXPIRED") {
          this._fail(
            new RightraPlaybackError(
              "SESSION_EXPIRED",
              "This playback session has expired. Please start it again."
            )
          );
        }
      });
  }

  _reportDownloadAttempt(type) {
    if (!this.sessionId || this._destroyed) return;
    const t = Date.now();
    if (this._lastReport[type] && t - this._lastReport[type] < 3000) return;
    this._lastReport[type] = t;
    this.client.sendDownloadAttempt({ session_id: this.sessionId, attempt_type: type }).catch(() => {});
  }

  _fail(err) {
    if (this._failed || this._destroyed) return;
    this._failed = true;
    this._abort?.abort();
    clearInterval(this._heartbeat);
    this._heartbeat = null;
    try { this.video.pause(); } catch {}
    console.error("[RightraPlayer]", err);
    const e = err instanceof RightraPlaybackError
      ? err
      : new RightraPlaybackError("UNKNOWN", err?.message || "Playback failed.", err);
    this._showError(e);
    this.onError(e);
  }

  get element() { return this.video; }

  async play() {
    if (this._holdReasons.size || this._failed || this._destroyed) return false;
    try {
      if (this.video.ended) this.video.currentTime = 0;
      await this.video.play();
      return true;
    } catch (err) {
      console.warn("[RightraPlayer] play() was blocked:", err);
      return false;
    }
  }

  async destroy() {
    if (this._destroyed) return;
    const seconds = Math.floor(this.video.currentTime || 0);
    this._destroyed = true;
    this._abort?.abort();
    clearInterval(this._heartbeat);
    clearTimeout(this._idleTimer);
    clearTimeout(this._tapTimer);
    clearTimeout(this._toastTimer);
    if (this._raf && typeof cancelAnimationFrame === "function") cancelAnimationFrame(this._raf);
    for (const w of [...this._waiters]) w();
    try { this._ro?.disconnect(); } catch {}
    this.security?.detach();
    this.audioWatermark?.detach();
    this.visualWatermark?.detach();
    this.videoWatermark?.detach();
    if (this.keyBytes) { this.keyBytes.fill(0); this.keyBytes = null; }
    for (const off of this._cleanup) { try { off(); } catch {} }
    this._cleanup = [];

    const fs = document.fullscreenElement || document.webkitFullscreenElement;
    if (fs === this.container) {
      try { (document.exitFullscreen || document.webkitExitFullscreen)?.call(document); } catch {}
    }
    if (this._pfs) document.documentElement.classList.remove("rtr-noscroll");

    if (this.sessionId && !this._endedSent) {
      this._endedSent = true;
      this.client
        .sendPlaybackEvent({ session_id: this.sessionId, event: "ended", seconds_watched: seconds })
        .catch(() => {});
    }
    try {
      this.video.pause();
      this.video.removeAttribute("src");
      this.video.load();
    } catch {}
    if (this._objectUrl) URL.revokeObjectURL(this._objectUrl);

    for (const n of this._nodes) n.parentNode?.removeChild(n);
    this._nodes = [];
    const c = this.container;
    c.classList.remove(
      "rtr-root", "rtr-pos", "rtr-paused", "rtr-loading", "rtr-idle", "rtr-error", "rtr-hold",
      "rtr-fs", "rtr-pfs", "rtr-fetching", "rtr-compact", "rtr-tiny", "rtr-novol", "rtr-scrub"
    );
    const restore = (name, value) => (value === null ? c.removeAttribute(name) : c.setAttribute(name, value));
    restore("tabindex", this._prev.tabindex);
    restore("role", this._prev.role);
    restore("aria-label", this._prev.label);
    this.key = null;
  }
}